import React from 'react';
import {
  BorrowerFallbackArt,
  ChooseAccountArt,
  ConfirmArt,
  CreateLinkArt,
  InstitutionArt,
  ManualFallbackArt,
  OpenLinkArt,
  SignInArt,
  StartPlaidArt,
} from './achGuideArt';
import { AchGuideVariant } from './achGuideStorage';

export type GuideStep = {
  id: string;
  title: string;
  body: string;
  tip: string;
  Art: React.ComponentType;
};

/**
 * The guide for staff, on the ACH Consent tab: what they do and what the borrower goes through, start to finish.
 * To change the wording or the order, edit here only.
 */
export const ACH_GUIDE_STEPS_STAFF: GuideStep[] = [
  {
    id: 'create-link',
    title: 'Create the link',
    body:
      'Create Link makes a link that opens the Plaid bank connection. Copy it to share yourself, or use Send Email. ' +
      'The status line tells you which kind of link is open and when it expires.',
    tip: 'Every new link replaces the open one, so the borrower always has just one working link.',
    Art: CreateLinkArt,
  },
  {
    id: 'open-link',
    title: 'Opens the secure link',
    body:
      'The borrower lands on a short welcome page from your company, then continues to the ACH consent form. ' +
      'There is nothing to log in to.',
    tip: 'The page shows the lender name and when the link expires.',
    Art: OpenLinkArt,
  },
  {
    id: 'start-plaid',
    title: 'Starts the Plaid connection',
    body:
      'Connect your bank account opens Plaid, a secure window run by Plaid. Its first screen asks for a phone number. ' +
      'Way A: enter it, and Plaid logs in a returning Plaid user and shows the accounts they saved, or signs them up ' +
      'to go faster next time. Way B: click Continue without phone number to connect through the bank, which the ' +
      'next steps show.',
    tip: 'Plaid handles the phone number and the bank login. We never receive either.',
    Art: StartPlaidArt,
  },
  {
    id: 'select-institution',
    title: 'Selects the institution',
    body:
      'After Continue without phone number, Plaid shows Select your institution: a list of popular banks and a search ' +
      'box. The borrower picks their bank, or searches for it by name.',
    tip: "A bank that Plaid doesn't support won't show up in the search. That borrower needs a manual link (last step).",
    Art: InstitutionArt,
  },
  {
    id: 'sign-in',
    title: 'Signs in at the bank',
    body:
      "Plaid asks the borrower to log in to the bank. At many banks, Continue to log in opens the bank's own sign-in " +
      'window, where they type their username and password. Other banks ask for the username and password inside Plaid.',
    tip: "We never see the borrower's bank password; at a bank with its own window it is typed on the bank's page.",
    Art: SignInArt,
  },
  {
    id: 'choose-account',
    title: 'Chooses the account and allows access',
    body:
      'After signing in, the borrower chooses the one account that will pay the loan, once. Banks with their own page ' +
      'ask for it there, and then the borrower ticks what to allow: account and routing number, account holder names ' +
      "and roles, and profile information. At other banks it is Plaid's Select an account screen. Only the accounts " +
      'we allow are listed, which are checking and savings, and only one account is linked.',
    tip:
      "If the account and routing number box isn't ticked, Plaid can't read the account and the borrower may be told " +
      'to contact you (last step). Plaid then checks the account and returns the borrower to the form.',
    Art: ChooseAccountArt,
  },
  {
    id: 'confirm-details',
    title: 'Confirms the details',
    body:
      'Back on the form, the chosen bank and the last four digits are shown, with Change if they picked the wrong one. ' +
      'The account holder name comes from Plaid, so they only pick the monthly payment date and continue to sign.',
    tip: 'The signed ACH form is what you see on the task afterwards.',
    Art: ConfirmArt,
  },
  {
    id: 'manual-fallback',
    title: "When the connection doesn't work",
    body:
      "Some banks can't be connected. The borrower then sees a message to contact you. Use Create Manual Link: " +
      'the borrower gets the form where they type their bank details instead of connecting.',
    tip:
      'Create Manual Link appears once a first link exists. Send Email always sends the Plaid link, and any new link ' +
      'replaces the open one.',
    Art: ManualFallbackArt,
  },
];

/**
 * The shorter guide for the borrower, on the public ACH page: just the Plaid connection, and what to do if it fails.
 * Written to "you".
 */
export const ACH_GUIDE_STEPS_BORROWER: GuideStep[] = [
  {
    id: 'start-plaid',
    title: 'Start the connection',
    body:
      "Connect your bank account opens Plaid, a secure window run by Plaid. Its first screen asks for a phone number. " +
      "Way A: enter it, and Plaid logs you in if you've used Plaid before and shows the accounts you saved, or signs " +
      'you up to go faster next time. Way B: click Continue without phone number to connect through your bank, which ' +
      'the next steps show.',
    tip: 'Plaid handles your phone number and your bank login. Your lender never receives either.',
    Art: StartPlaidArt,
  },
  {
    id: 'select-institution',
    title: 'Select your bank',
    body:
      'Plaid shows Select your institution: a list of popular banks and a search box. Pick your bank, or search for it ' +
      'by name.',
    tip: "If your bank isn't there, it can't be connected. Your lender can send you a link to type your bank details instead (last step).",
    Art: InstitutionArt,
  },
  {
    id: 'sign-in',
    title: 'Sign in to your bank',
    body:
      "Plaid asks you to log in to your bank. At many banks, Continue to log in opens your bank's own sign-in window, " +
      'where you type your username and password. Other banks ask for them inside Plaid.',
    tip: "Your lender never sees your bank password. With your bank's own window, you type it on your bank's page.",
    Art: SignInArt,
  },
  {
    id: 'choose-account',
    title: 'Choose your account and allow access',
    body:
      'After signing in, choose the one account that will pay your loan, once. Banks with their own page ask for it ' +
      'there, and then you tick what to allow: account and routing number, account holder names and roles, and profile ' +
      "information. At other banks it is Plaid's Select an account screen. Only checking and savings accounts are " +
      'listed, and only one account is linked.',
    tip:
      "Tick the account and routing number box, or Plaid can't read your account and you may be asked to contact your " +
      'lender. Plaid then checks the account and brings you back to this form.',
    Art: ChooseAccountArt,
  },
  {
    id: 'manual-fallback',
    title: "If your bank can't be connected",
    body:
      "Some banks can't be connected. You'll see a message to contact your lender. Your lender can send you a new link " +
      'where you type your bank details yourself, instead of connecting.',
    tip: 'The new link replaces this one, so use the newest link you receive.',
    Art: BorrowerFallbackArt,
  },
];

export const ACH_GUIDE_STEPS: { [variant in AchGuideVariant]: GuideStep[] } = {
  staff: ACH_GUIDE_STEPS_STAFF,
  borrower: ACH_GUIDE_STEPS_BORROWER,
};
